/**
 * Personal landing page for members without `reporting.dashboard.view`
 * (technicians, vendors, requesters): the open work assigned to them and the
 * fault reports they raised, with direct links to act on each.
 */
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { useRequests } from "@/features/requests/queries";
import { useWorkOrders } from "@/features/workorders/queries";
import {
  EmptyState,
  KpiCard,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import { formatDate } from "@/shared/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/primitives";

/** "My work" landing. */
export function MyWorkPage(): ReactElement {
  const { t } = useTranslation();
  const { me, can } = useAuth();
  const canSeeWork =
    can("workorders.workorder.view_assigned") || can("workorders.workorder.view_all");
  const canSeeRequests = can("requests.request.view_own") || can("requests.request.view_all");
  const wos = useWorkOrders({ mine: true, open_only: true });
  const reqs = useRequests({ mine: true, open_only: true });

  const inProgress = wos.items.filter((w) => w.status === "in_progress").length;
  const overdue = wos.items.filter(
    (w) => w.due_at !== null && new Date(w.due_at).getTime() < Date.now(),
  ).length;

  return (
    <div>
      <PageHeader
        title={t("myWork.title")}
        subtitle={me ? `${me.display_name} · ${me.active_tenant.name}` : undefined}
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label={t("myWork.assigned")}
          value={wos.total ?? wos.items.length}
          to="/work-orders?mine=true"
        />
        <KpiCard label={t("myWork.inProgress")} value={inProgress} tone="info" />
        <KpiCard
          label={t("myWork.overdue")}
          value={overdue}
          tone={overdue > 0 ? "danger" : "neutral"}
        />
        <KpiCard
          label={t("myWork.myReports")}
          value={reqs.total ?? reqs.items.length}
          to="/requests?mine=true"
        />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {canSeeWork && (
          <Card>
            <CardHeader>
              <CardTitle>{t("myWork.assignedWorkOrders")}</CardTitle>
            </CardHeader>
            <CardContent>
              {wos.query.isPending ? (
                <LoadingState />
              ) : wos.items.length === 0 ? (
                <EmptyState message={t("myWork.nothingAssigned")} />
              ) : (
                <ul className="divide-y">
                  {wos.items.slice(0, 20).map((w) => (
                    <li key={w.id} className="flex flex-wrap items-center gap-2 py-3 text-sm">
                      <div className="min-w-0 basis-full sm:flex-1 sm:basis-auto">
                        <Link
                          to="/work-orders/$woId"
                          params={{ woId: w.id }}
                          className="min-w-0 basis-full break-words hover:underline sm:flex-1 sm:basis-auto"
                        >
                          <span className="font-mono text-xs text-muted-foreground">{w.code}</span>{" "}
                          {w.title}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          <AssetReference
                            id={w.asset.id}
                            name={w.asset.name}
                            context="work-order"
                          />
                          {w.due_at ? ` · ${t("app.due")} ${formatDate(w.due_at)}` : ""}
                        </div>
                      </div>
                      <StatusBadge value={w.priority} namespace="workOrders.priority" />
                      <StatusBadge value={w.status} namespace="workOrders.status" />
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
        {canSeeRequests && (
          <Card>
            <CardHeader>
              <CardTitle>{t("myWork.myOpenReports")}</CardTitle>
            </CardHeader>
            <CardContent>
              {reqs.query.isPending ? (
                <LoadingState />
              ) : reqs.items.length === 0 ? (
                <EmptyState message={t("myWork.noReports")} />
              ) : (
                <ul className="divide-y">
                  {reqs.items.slice(0, 20).map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-2 py-3 text-sm">
                      <div className="min-w-0 basis-full sm:flex-1 sm:basis-auto">
                        <Link
                          to="/requests/$requestId"
                          params={{ requestId: r.id }}
                          className="min-w-0 basis-full break-words hover:underline sm:flex-1 sm:basis-auto"
                        >
                          <span className="font-mono text-xs text-muted-foreground">{r.code}</span>{" "}
                          {r.title}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          <AssetReference id={r.asset_id} name={r.asset_name ?? ""} />
                        </div>
                      </div>
                      <StatusBadge value={r.severity} namespace="requests.severity" />
                      <StatusBadge value={r.status} namespace="requests.status" />
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
